
import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Calculator, Menu, X } from "lucide-react";

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);

  const navLinks = [
    { to: "/", label: "Home" },
    { to: "/about", label: "About" },
    { to: "/loan-products", label: "Services" },
    { to: "/calculators", label: "Calculators" },
    { to: "/resources", label: "Resources" },
    { to: "/success-stories", label: "Success Stories" },
    { to: "/contact", label: "Contact" },
  ];

  const location = useLocation();



  return (
    <nav className="fixed top-0 z-50 w-full backdrop-blur-lg bg-slate-900/80 border-b border-blue-500/20">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center">
            <Link to="/" className="flex items-center space-x-2">
              <img
                src="/images/logo.png"
                alt="White Pearl Finance Logo"
                className="h-10 w-auto"
              />
              <span className="text-white text-xl font-semibold ml-2"></span>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-1">
            {navLinks.map((link) => {
              const isActive = location.pathname === link.to;
              return (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`
                    relative px-4 py-2 text-sm font-medium transition-all duration-200
                    ${isActive
                      ? 'text-white'
                      : 'text-gray-300 hover:text-white'}
                    group
                  `}
                >
                  <span className="relative z-10">{link.label}</span>
                  <span className={`
                    absolute bottom-0 left-0 w-full h-0.5 bg-blue-400 transition-all duration-300
                    ${isActive ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'}
                  `}></span>
                  <span className={`
                    absolute inset-0 bg-gradient-to-r from-blue-500/10 to-blue-500/5 rounded-md
                    opacity-0 group-hover:opacity-100 transition-opacity duration-200
                  `}></span>
                </Link>
              );
            })}
          </div>

          {/* Desktop Buttons */}
          <div className="hidden md:flex items-center space-x-3">
            <Button
              variant="outline"
              size="sm"
              className="text-xs lg:text-sm border-blue-500 text-blue-400 hover:bg-blue-500/10"
              asChild
            >
              <Link to="/calculators">
                <Calculator className="h-4 w-4 mr-2" />
                Quick Calculator
              </Link>
            </Button>
            <Button
              className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
              asChild
            >
              <Link to="/contact?source=navbar_apply_now">
                Apply Now
              </Link>
            </Button>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsOpen(!isOpen)}
              className="text-gray-300 hover:bg-slate-800/50"
            >
              {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </Button>
          </div>

          {/* Mobile Navigation */}
          <div className={`md:hidden ${isOpen ? 'block' : 'hidden'}`}>
            <div className="px-2 pt-2 pb-4 space-y-1 bg-slate-800/95 backdrop-blur-lg rounded-lg mt-2">
              {/* Mobile menu items */}
              <div className="px-2 pt-2 pb-3 space-y-1">
                {navLinks.map((link) => {
                  const isActive = location.pathname === link.to;
                  return (
                    <Link
                      key={link.to}
                      to={link.to}
                      onClick={() => setIsOpen(false)}
                      className={`block px-4 py-3 text-base font-medium rounded-md transition-colors duration-200
                        ${isActive
                          ? 'bg-blue-500/20 text-white'
                          : 'text-gray-300 hover:bg-slate-700/50'}`}
                    >
                      <div className="flex items-center">
                        <span className={`mr-2 h-1 w-1 rounded-full ${isActive ? 'bg-blue-400' : 'bg-transparent'}`}></span>
                        {link.label}
                      </div>
                    </Link>
                  );
                })}
              </div>

              {/* Action Buttons */}
              <div className="pt-2 space-y-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full border-blue-500 text-blue-400 hover:bg-blue-500/10"
                  asChild
                >
                  <Link to="/calculators" onClick={() => setIsOpen(false)} className="flex items-center justify-center">
                    <Calculator className="h-4 w-4 mr-2" />
                    Quick Calculator
                  </Link>
                </Button>
                <Button
                  className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                  asChild
                >
                  <Link to="/contact?source=mobile_nav_apply_now" onClick={() => setIsOpen(false)}>
                    Apply Now
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};
